import { ResourceType, coco } from '@cotera/api';
import { resourceKind } from '@cotera/sdk/core';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import { getArtifactRenderer } from './registry';
import type { ArtifactDescriptor } from './types';

/**
 * Shared helpers for turning a workspace-resource `@` mention into an artifact the
 * artifact panel can show. Used by both coco chat (persisted artifacts) and the
 * editor pages (transient artifact panel).
 */

export type ParsedResourceMention = {
  resourceType: ResourceType;
  resourceId: string;
};

const { isWorkflowToolKey, workflowIdFromToolKey } = coco;

const isResourceType = (value: string): value is ResourceType =>
  (Object.values(ResourceType) as string[]).includes(value);

/**
 * Extract `{ resourceType, resourceId }` from a clicked workspace-resource mention,
 * or `null` if the click isn't a resolvable resource mention. Mirrors the parsing
 * in `use-resource-mention-navigation.ts`.
 *
 * Supports:
 * - Modern `t: "resource"` mentions with nested `{ resourceType, resourceId }`
 * - Legacy `t: "agent"` / `t: "document"` mentions (id from `referenceId` or `name`)
 */
export const parseResourceMention = (
  data: MentionClickData
): ParsedResourceMention | null => {
  if (data.t === 'agent' || data.t === 'document') {
    const resourceId =
      data.referenceId !== undefined && data.referenceId.trim() !== ''
        ? data.referenceId.trim()
        : data.name.trim() !== ''
        ? data.name.trim()
        : null;
    if (resourceId === null) {
      return null;
    }
    return {
      resourceType:
        data.t === 'agent' ? ResourceType.Agent : ResourceType.Document,
      resourceId,
    };
  }

  if (data.t !== 'resource') {
    return null;
  }
  const inner = data.data;
  if (inner === undefined) {
    return null;
  }
  const { resourceType, resourceId } = inner;
  if (
    typeof resourceType !== 'string' ||
    !isResourceType(resourceType) ||
    typeof resourceId !== 'string' ||
    resourceId === ''
  ) {
    return null;
  }
  return { resourceType, resourceId };
};

/**
 * Extract a workflow UUID from a clicked workflow-tool `@` mention
 * (`coco/workflow-<compact-uuid>`), or `null` if the click isn't one.
 * Mirrors the agent-tool handling in coco chat mention clicks.
 */
export const parseWorkflowToolMention = (
  data: MentionClickData
): string | null => {
  if (data.t !== 'tool') {
    return null;
  }
  const rawKey = data.toolKey ?? data.name;
  if (rawKey === undefined || rawKey === '') {
    return null;
  }
  const toolKey = rawKey.replaceAll('_', '/');
  if (!isWorkflowToolKey(toolKey)) {
    return null;
  }
  return workflowIdFromToolKey(toolKey);
};

/** True when the artifact viewer has a renderer registered for this resource type. */
export const isArtifactViewable = (resourceType: ResourceType): boolean =>
  getArtifactRenderer(resourceType) !== null;

/**
 * The workspace {@link ResourceType} an artifact type stands for, or `null` when
 * the type is panel-local — a sub-view of another artifact (`workflow-node`) or a
 * rendering of tool output (charts) — with no saved resource behind it.
 */
export const artifactTypeAsResourceType = (
  artifactType: string
): ResourceType | null =>
  isResourceType(artifactType) && isArtifactViewable(artifactType)
    ? artifactType
    : null;

/**
 * The `payload`/`data` object for a resource artifact, keyed the way each renderer
 * expects (`agentId`/`documentId`/`folderId`/`workflowId`). Renderers also fall
 * back to `artifact.artifactId`, but we set the type-specific key for clarity.
 */
export const resourceArtifactData = (
  resourceType: ResourceType,
  resourceId: string,
  name?: string
): Record<string, string> => {
  const { artifact } = resourceKind(resourceType);
  if (artifact === null) {
    return {};
  }
  return {
    [artifact.idKey]: resourceId,
    // Folders are the one type whose payload carries a name, so the panel can
    // title the artifact before the resource loads.
    ...(resourceType === ResourceType.Folder &&
    name !== undefined &&
    name !== ''
      ? { name }
      : {}),
  };
};

/**
 * The resource id an artifact payload carries, falling back to the artifact's
 * own id — which renderers already treat as the resource id.
 *
 * Each artifact type keys its payload differently (`agentId`, `datasetId`, …);
 * the key comes from the type's {@link ResourceKind} rather than being spelled
 * out again per call site.
 */
/**
 * The workspace resource an artifact stands for, or null when it stands for
 * nothing shareable.
 *
 * Charts, dashboards and workflow steps live only inside the conversation that
 * made them: they have no resource row, no id anyone else could be granted, and
 * so nothing a Share could act on. Every surface offering to share an artifact
 * has to ask this first, or it offers a button that cannot work.
 */
export const shareableResourceForArtifact = (artifact: {
  type: string;
  payload: unknown;
  artifactId: string;
}): { type: ResourceType; id: string } | null => {
  const resourceType = artifactTypeAsResourceType(artifact.type);
  if (resourceType === null) {
    return null;
  }
  const id = resourceIdFromArtifactPayload(
    artifact.type,
    artifact.payload,
    artifact.artifactId
  );
  return id === null ? null : { type: resourceType, id };
};

export const resourceIdFromArtifactPayload = (
  artifactType: string,
  payload: unknown,
  fallbackId: string
): string | null => {
  const resourceType = artifactTypeAsResourceType(artifactType);
  const artifact =
    resourceType === null ? null : resourceKind(resourceType).artifact;
  if (artifact !== null && typeof payload === 'object' && payload !== null) {
    const fromPayload = (payload as Record<string, unknown>)[artifact.idKey];
    if (typeof fromPayload === 'string' && fromPayload !== '') {
      return fromPayload;
    }
  }
  return fallbackId !== '' ? fallbackId : null;
};

/**
 * Build an `ArtifactDescriptor` for a workspace resource. `artifactId` is the
 * resource id so the panel de-dupes by it and renderers can fall back to it.
 * `messageId`/`toolCallId`/`toolName` are empty for non-conversation (transient) use.
 */
export const buildResourceArtifactDescriptor = (
  resourceType: ResourceType,
  resourceId: string,
  name?: string,
  /** Merged over the type's default payload, e.g. `{ isNew: true }`. */
  payload?: Record<string, unknown>
): ArtifactDescriptor => ({
  type: resourceType,
  artifactId: resourceId,
  payload: {
    ...resourceArtifactData(resourceType, resourceId, name),
    ...payload,
  },
  messageId: '',
  toolCallId: '',
  toolName: '',
});
